'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { KeyboardShortcutRegistryContext, ShortcutRegistrationsContext, useShortcutRegistryState, } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/internal/registryState.js';
import { resolveMatch, SHORTCUT_YIELD, } from '#pplx/frontend/apps/ask/keyboard-shortcuts-provider/registry.js';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
/**
 * Global keyboard shortcut provider.
 *
 * Manages a **stack** of shortcuts: the most recently registered handler for
 * a matching key combo wins. This enables context-based overrides — when a
 * new route or component mounts and registers a keybinding that already
 * exists, it takes precedence. On unmount, the previous handler resumes.
 *
 * This provider is a **pure registry/dispatcher** — it does not register any
 * shortcuts itself. Entry points that need built-in shortcuts (new thread,
 * show shortcuts, dictation) should render `<BuiltInShortcuts />` as a child.
 * This decouples the provider from heavy context dependencies
 * (e.g. `QueryInputStateProvider`) that lightweight entry points like
 * `spa-aux` (auth/login/signup) don't have.
 *
 * The dispatcher only selects and invokes matching handlers; it does not call
 * `event.preventDefault()` automatically. Handlers should prevent default browser
 * behavior themselves when needed.
 *
 * Also exposes the current registrations list via
 * `ShortcutRegistrationsContext` so that the keyboard shortcuts modal
 * can dynamically render all labeled shortcuts.
 */
export const KeyboardShortcutsProvider = ({ children, isMacOS = false, isMobileDevice = false, onShortcutFired }) => {
    const { registrationsRef, registrations, register, subscribe, getDisplayKeybinding } = useShortcutRegistryState();
    const onShortcutFiredRef = useRef(onShortcutFired);
    onShortcutFiredRef.current = onShortcutFired;
    const handleDocumentKeyDown = useCallback((event) => {
        const stack = registrationsRef.current;
        // Iterate in reverse: last registered = highest priority (stack top).
        for (let i = stack.length - 1; i >= 0; i--) {
            const reg = stack[i];
            // Skip disabled registrations – the event falls through to the
            // next handler in the stack as if this one wasn't there.
            if (reg.isEnabled === false)
                continue;
            const match = resolveMatch(reg);
            if (match(event)) {
                const result = reg.handler(event);
                // By default, first match wins — the handler is considered to have
                // consumed the event. A handler can opt out by returning
                // `SHORTCUT_YIELD`, which makes dispatch continue to the next
                // lower-priority handler sharing the same keybinding.
                if (result === SHORTCUT_YIELD)
                    continue;
                onShortcutFiredRef.current?.(reg, event);
                return;
            }
        }
    }, [registrationsRef]);
    useEffect(() => {
        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => {
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [handleDocumentKeyDown]);
    const registryValue = useMemo(() => ({ register, subscribe, getDisplayKeybinding, isMacOS, isMobileDevice }), [register, subscribe, getDisplayKeybinding, isMacOS, isMobileDevice]);
    return (_jsx(KeyboardShortcutRegistryContext.Provider, { value: registryValue, children: _jsx(ShortcutRegistrationsContext.Provider, { value: registrations, children: children }) }));
};
//# sourceMappingURL=KeyboardShortcutsProvider.js.map